import { SupportedDemandType } from 'enums/SupportedDemandType';
import { useEffect } from 'react';
import { demandService } from 'services/DemandService';
import { CalculatePricing, VehicleBasedDemandState } from 'types/demand.type';
import { ExtraWithUnit } from 'types/vehicle.type';
import { useDebouncedCallback } from 'use-debounce';
import { useCheckPricingExists } from './useCheckPricingExists';

export function usePriceWidgetCalculateAmounts(
  vehicleId: number,
  supportedServices: ExtraWithUnit[],
  supportedPackages: ExtraWithUnit[],
  setPricing: (val: CalculatePricing.MappedResponseWithDuration) => void,
  supportedDemandTypes: SupportedDemandType[],
  validateForm: (
    state: VehicleBasedDemandState,
    showError: boolean
  ) => Promise<boolean>,
  setIsCalculateAmountRequestSent: (val: boolean) => void,
  setIsCalcutePricingRequestOngoing: (val: boolean) => void,
  pricing: CalculatePricing.MappedResponseWithDuration | undefined
) {
  const { checkPricingExists } = useCheckPricingExists(supportedDemandTypes);

  const handleCalculateAmounts = async (state: VehicleBasedDemandState) => {
    if (!checkPricingExists()) return false;
    setIsCalcutePricingRequestOngoing(false);

    const localState = {
      ...state,
      numberOfPeople:
        +state.numberOfPeople === 0 ? '1' : `${state.numberOfPeople}`,
    };

    const isValid = await validateForm(localState, false);
    if (!isValid) return false;

    try {
      !pricing && setIsCalcutePricingRequestOngoing(true);

      const payload = demandService.formatVehicleBasedDemandStateToDemand(
        localState,
        vehicleId,
        [...supportedServices, ...supportedPackages],
        undefined
      );
      const pricings = await demandService.calculatePricing({
        ...payload,
        source: 'API',
      });
      setPricing({ ...pricings, duration: +state.duration });
      setIsCalculateAmountRequestSent(true);
    } finally {
      setIsCalcutePricingRequestOngoing(false);
    }
  };

  const debouncedHandleCalculateAmounts = useDebouncedCallback(
    handleCalculateAmounts,
    500
  );

  useEffect(() => {
    return () => {
      debouncedHandleCalculateAmounts.flush();
    };
  }, [debouncedHandleCalculateAmounts]);

  return {
    handleCalculateAmounts,
    debouncedHandleCalculateAmounts,
  };
}
